import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Mail, Save, Check, CalendarClock, Loader2, FileDown, UserRound,
  Sunrise,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { toast } from "sonner";
import api from "../../lib/api";

export default function AccountantContactCard({ restaurantId }) {
  const [loading, setLoading] = useState(true);
  const [savedEmail, setSavedEmail] = useState("");
  const [email, setEmail] = useState("");
  const [autoSend, setAutoSend] = useState(false);
  const [dailyAutoSend, setDailyAutoSend] = useState(false);
  const [dailyHourUtc, setDailyHourUtc] = useState(22);
  const [lastAutoSend, setLastAutoSend] = useState(null);
  const [lastDailyAutoSend, setLastDailyAutoSend] = useState(null);
  const [savingEmail, setSavingEmail] = useState(false);
  const [savingAuto, setSavingAuto] = useState(false);
  const [savingDaily, setSavingDaily] = useState(false);
  const [ccText, setCcText] = useState("");
  const [savedCcList, setSavedCcList] = useState([]);
  const [savingCc, setSavingCc] = useState(false);

  useEffect(() => {
    if (!restaurantId) return;
    setLoading(true);
    api.getAccountantEmail(restaurantId)
      .then((r) => {
        setSavedEmail(r.accountant_email || "");
        setEmail(r.accountant_email || "");
        setAutoSend(!!r.accountant_auto_send);
        setDailyAutoSend(!!r.daily_report_auto_send);
        setDailyHourUtc(Number.isInteger(r.daily_report_hour_utc) ? r.daily_report_hour_utc : 22);
        setLastAutoSend(r.last_auto_send || null);
        setLastDailyAutoSend(r.last_daily_auto_send || null);
        const cc = r.accountant_cc || [];
        setSavedCcList(cc);
        setCcText(cc.join(", "));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [restaurantId]);

  const emailChanged = email.trim() !== savedEmail;
  const ccListFromInput = ccText.split(",").map(s => s.trim()).filter(Boolean);
  const ccChanged = JSON.stringify(ccListFromInput) !== JSON.stringify(savedCcList);

  const persistCc = async () => {
    setSavingCc(true);
    try {
      const r = await api.setAccountantEmail(restaurantId, { accountant_cc: ccListFromInput });
      setSavedCcList(r.accountant_cc || []);
      setCcText((r.accountant_cc || []).join(", "));
      toast.success(
        (r.accountant_cc || []).length
          ? `CC guardado (${(r.accountant_cc || []).length} emails)`
          : "CC eliminado",
      );
    } catch (e) {
      toast.error(e?.message || "No se pudo guardar el CC");
    } finally {
      setSavingCc(false);
    }
  };

  const persistEmail = async () => {
    const clean = email.trim();
    setSavingEmail(true);
    try {
      const r = await api.setAccountantEmail(restaurantId, { accountant_email: clean });
      setSavedEmail(r.accountant_email || "");
      setEmail(r.accountant_email || "");
      toast.success(clean ? "Email del contable guardado" : "Email del contable eliminado");
    } catch (e) {
      toast.error(e?.message || "No se pudo guardar el email");
    } finally {
      setSavingEmail(false);
    }
  };

  const toggleAutoSend = async (checked) => {
    setSavingAuto(true);
    try {
      if (checked && emailChanged) await persistEmail();
      const r = await api.setAccountantEmail(restaurantId, {
        accountant_auto_send: checked,
      });
      setAutoSend(!!r.accountant_auto_send);
      toast.success(
        checked
          ? "Envío automático activado (día 3 de cada mes)"
          : "Envío automático desactivado",
      );
    } catch (e) {
      toast.error(e?.message || "No se pudo actualizar la preferencia");
    } finally {
      setSavingAuto(false);
    }
  };

  const toggleDailyAutoSend = async (checked) => {
    setSavingDaily(true);
    try {
      if (checked && emailChanged) await persistEmail();
      const r = await api.setAccountantEmail(restaurantId, {
        daily_report_auto_send: checked,
      });
      setDailyAutoSend(!!r.daily_report_auto_send);
      toast.success(
        checked
          ? `Envío automático diario activado (${String(dailyHourUtc).padStart(2, "0")}:00 UTC)`
          : "Envío automático diario desactivado",
      );
    } catch (e) {
      toast.error(e?.message || "No se pudo actualizar la preferencia");
    } finally {
      setSavingDaily(false);
    }
  };

  const saveDailyHour = async (newHour) => {
    const h = Math.max(0, Math.min(23, parseInt(newHour, 10) || 0));
    setDailyHourUtc(h);
    if (!dailyAutoSend) return; // only persist if the feature is active
    setSavingDaily(true);
    try {
      await api.setAccountantEmail(restaurantId, { daily_report_hour_utc: h });
      toast.success(`Hora de envío actualizada: ${String(h).padStart(2, "0")}:00 UTC`);
    } catch (e) {
      toast.error(e?.message || "No se pudo guardar la hora");
    } finally {
      setSavingDaily(false);
    }
  };

  return (
    <Card data-testid="accountant-contact-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <UserRound className="h-4 w-4 text-primary" />
          Contacto del contable
        </CardTitle>
        <CardDescription>
          Email y envío automático del cierre contable mensual. Desde aquí controlas
          a quién y cómo se enviarán las exportaciones contables de este restaurante.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {loading ? (
          <div className="flex justify-center py-4">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            {/* Email */}
            <div className="space-y-1.5">
              <Label htmlFor="settings-accountant-email" className="text-xs font-medium flex items-center gap-1.5">
                <Mail className="h-3.5 w-3.5" />
                Email del contable
              </Label>
              <div className="flex gap-2">
                <Input
                  id="settings-accountant-email"
                  type="email"
                  placeholder="contable@ejemplo.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={savingEmail}
                  data-testid="settings-accountant-email-input"
                />
                {emailChanged && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={persistEmail}
                    disabled={savingEmail}
                    data-testid="settings-accountant-email-save-btn"
                  >
                    {savingEmail
                      ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      : <><Save className="h-3.5 w-3.5 mr-1" /> Guardar</>}
                  </Button>
                )}
              </div>
              {savedEmail && !emailChanged && (
                <p className="text-[11px] text-emerald-700 flex items-center gap-1">
                  <Check className="h-3 w-3" /> Email guardado
                </p>
              )}
              {!savedEmail && !emailChanged && (
                <p className="text-[11px] text-muted-foreground">
                  Aún no has configurado el email. Guárdalo para poder enviar exportaciones manuales o activar el envío automático.                </p>
              )}
            </div>

            {/* CC default */}
            <div className="space-y-1" data-testid="settings-accountant-cc">
              <Label htmlFor="settings-accountant-cc-input" className="text-sm font-medium flex items-center gap-1.5">
                <Mail className="h-3.5 w-3.5 text-indigo-600" /> CC por defecto (opcional)
              </Label>
              <div className="flex gap-2">
                <Input
                  id="settings-accountant-cc-input"
                  type="text"
                  placeholder="duen1@tu-restaurante.com, auditor@gestoria.com"
                  value={ccText}
                  onChange={(e) => setCcText(e.target.value)}
                  disabled={savingCc}
                  data-testid="settings-accountant-cc-input"
                />
                {ccChanged && (
                  <Button
                    type="button" variant="outline" size="sm"
                    onClick={persistCc}
                    disabled={savingCc}
                    data-testid="settings-accountant-cc-save-btn"
                  >
                    {savingCc
                      ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      : <><Save className="h-3.5 w-3.5 mr-1" /> Guardar</>}
                  </Button>
                )}
              </div>
              <p className="text-[11px] text-muted-foreground">
                {savedCcList.length > 0 && !ccChanged
                  ? `${savedCcList.length} email${savedCcList.length === 1 ? "" : "s"} en copia. Se aplicarán a todos los envíos (manual y automático).`
                  : "Se usará como copia en cada email al contable. Sepáralos con coma. Máx 5."}
              </p>
            </div>

            {/* Auto-send */}
            <div
              className="rounded-lg border border-indigo-200 bg-indigo-50/60 p-3 space-y-2"
              data-testid="settings-accountant-autosend"
            >
              <div className="flex items-start gap-3">
                <CalendarClock className="h-5 w-5 text-indigo-600 mt-0.5 shrink-0" />
                <div className="flex-1">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="text-sm font-semibold text-indigo-900">
                        Envío automático mensual
                      </div>
                      <p className="text-[11px] text-indigo-800/80 mt-0.5">
                        El día 3 de cada mes enviamos el ZIP del mes anterior al email guardado.
                      </p>
                    </div>
                    <Switch
                      checked={autoSend}
                      onCheckedChange={toggleAutoSend}
                      disabled={savingAuto || (!autoSend && !savedEmail)}
                      data-testid="settings-accountant-autosend-switch"
                    />
                  </div>
                  {autoSend && savedEmail && (
                    <p className="text-[11px] text-indigo-700 mt-1.5">
                      Se enviará a <b>{savedEmail}</b>.
                    </p>
                  )}
                  {!savedEmail && (
                    <p className="text-[11px] text-amber-700 mt-1.5">
                      Guarda primero el email del contable para activar el envío automático.
                    </p>
                  )}
                  {lastAutoSend && (
                    <p className="text-[11px] text-indigo-600/80 mt-1.5">
                      Último envío automático: <b>{lastAutoSend.month_key}</b> a {lastAutoSend.sent_to}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Daily auto-send */}
            <div
              className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-3 space-y-2"
              data-testid="settings-accountant-daily-autosend"
            >
              <div className="flex items-start gap-3">
                <Sunrise className="h-5 w-5 text-emerald-700 mt-0.5 shrink-0" />
                <div className="flex-1">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="text-sm font-semibold text-emerald-900">
                        Envío automático diario
                      </div>
                      <p className="text-[11px] text-emerald-800/80 mt-0.5">
                        Cada noche enviamos al contable el PDF con el arqueo de caja, ventas por método y gastos en efectivo del día anterior.
                      </p>
                    </div>
                    <Switch
                      checked={dailyAutoSend}
                      onCheckedChange={toggleDailyAutoSend}
                      disabled={savingDaily || (!dailyAutoSend && !savedEmail)}
                      data-testid="settings-accountant-daily-autosend-switch"
                    />
                  </div>
                  <div className="flex items-center gap-2 mt-2.5">
                    <Label htmlFor="settings-daily-hour-utc" className="text-[11px] font-medium text-emerald-900 whitespace-nowrap">
                      Hora envío (UTC):
                    </Label>
                    <Input
                      id="settings-daily-hour-utc"
                      type="number"
                      min={0}
                      max={23}
                      value={dailyHourUtc}
                      onChange={(e) => setDailyHourUtc(Math.max(0, Math.min(23, parseInt(e.target.value, 10) || 0)))}
                      onBlur={(e) => saveDailyHour(e.target.value)}
                      className="h-7 w-20 text-xs"
                      disabled={savingDaily}
                      data-testid="settings-daily-hour-utc-input"
                    />
                    <span className="text-[10px] text-emerald-700/80">
                      {`(${String(dailyHourUtc).padStart(2, "0")}:00 UTC ≈ ${String((dailyHourUtc + 1) % 24).padStart(2, "0")}:00 horario Madrid invierno)`}
                    </span>
                  </div>
                  {dailyAutoSend && savedEmail && (
                    <p className="text-[11px] text-emerald-700 mt-1.5">
                      Se enviará a <b>{savedEmail}</b> solo si el día tuvo ventas o caja cerrada.
                    </p>
                  )}
                  {!savedEmail && (
                    <p className="text-[11px] text-amber-700 mt-1.5">
                      Guarda primero el email del contable para activar el envío diario.
                    </p>
                  )}
                  {lastDailyAutoSend && (
                    <p className="text-[11px] text-emerald-700/80 mt-1.5">
                      Último envío diario: <b>{lastDailyAutoSend.day}</b> a {lastDailyAutoSend.sent_to}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* CTA: ir al módulo Contabilidad para un envío puntual */}
            <div className="flex items-center justify-between gap-3 rounded-lg border border-border bg-muted/40 p-3">
              <div className="text-xs text-muted-foreground">
                ¿Quieres enviar una exportación ahora mismo?
              </div>
              <Button
                asChild
                size="sm"
                variant="outline"
                data-testid="settings-accountant-go-to-finance"
              >
                <Link to="/app/finance">
                  <FileDown className="h-3.5 w-3.5 mr-1.5" />
                  Ir a Contabilidad
                </Link>
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
